import { useMemo, useState } from "react";

import { faSearch } from "@fortawesome/pro-regular-svg-icons";
import { find } from "lodash-es";
import { useDebounce } from "use-debounce";

import {
  useAllUsers,
  useCreateRole,
  useDeleteRole,
} from "src/adminPanel/queries";
import { Checkbox } from "src/base-components/Checkbox";
import { SearchBox } from "src/base-components/SearchBox";
import { EmptyState } from "src/design-system/EmptyState";
import { Modal } from "src/design-system/Modal";
import { useFuseSearch } from "src/utils/useFuseSearch";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  selectedOrg: string;
};

export const AddUserModal: React.FC<Props> = ({
  isOpen,
  onClose,
  selectedOrg,
}) => {
  const allUsers = useAllUsers();
  const createRole = useCreateRole();
  const deleteRole = useDeleteRole();
  const [search, setSearch] = useState("");

  const [debouncedSearch] = useDebounce(search, 500);

  const taktileUsers = useMemo(() => {
    return allUsers.data
      ?.filter((user) => user.email?.endsWith("@taktile.com"))
      .sort((a, b) => a.email?.localeCompare(b.email ?? "") ?? 0);
  }, [allUsers.data]);

  const fuseSearch = useFuseSearch(taktileUsers ?? [], {
    keys: ["email"],
    threshold: 0.5,
  });

  const filteredAndSortedUsers = fuseSearch(debouncedSearch);

  return (
    <Modal open={isOpen} size="lg" onClose={onClose}>
      <Modal.Header
        description={
          <div className="p-4">
            <SearchBox
              placeholder="Search by email"
              value={search}
              onChange={(e) => setSearch(e.toLowerCase())}
            />
          </div>
        }
      >
        Add user with fwd role to the organisation
      </Modal.Header>
      <Modal.Content>
        {filteredAndSortedUsers.length === 0 ? (
          <EmptyState
            description="Try adjusting the search to find what you are looking for"
            headline="No users found"
            icon={faSearch}
            size="compact"
          />
        ) : (
          <ul className="h-110 overflow-y-auto">
            <table className="border-spacing-2">
              <thead>
                <th className="px-4 py-2">Username</th>
                <th className="px-4 py-2">Email</th>
                <th className="py-2">fwd_d</th>
                <th className="px-2 py-2">fwd_viewer</th>
              </thead>
              <tbody>
                {filteredAndSortedUsers.map((user) => (
                  <tr key={user.id}>
                    <td key={`${user.id}-username`} className="px-4">
                      {user.username}
                    </td>
                    <td key={`${user.id}-email`} className="px-4">
                      {user.email}
                    </td>
                    <td>
                      {/* fwd_d */}
                      <Checkbox
                        checked={user.roles?.some(
                          (role) =>
                            role.name === "fwd_d" &&
                            role.arguments?.org_id === selectedOrg,
                        )}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          if (e.target.checked) {
                            createRole.mutate({
                              user_id: user.id,
                              arguments: {
                                org_id: selectedOrg,
                              },
                              name: "fwd_d",
                            });
                          } else {
                            const role = find(user.roles, {
                              name: "fwd_d",
                              arguments: {
                                org_id: selectedOrg,
                              },
                            });

                            if (role) {
                              deleteRole.mutate(role.id);
                            }
                          }
                        }}
                      />
                    </td>
                    <td>
                      {/* fwd_viewer */}
                      <Checkbox
                        checked={user.roles?.some(
                          (role) =>
                            role.name === "fwd_viewer" &&
                            role.arguments?.org_id === selectedOrg,
                        )}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          if (e.target.checked) {
                            createRole.mutate({
                              user_id: user.id,
                              arguments: {
                                org_id: selectedOrg,
                              },
                              name: "fwd_viewer",
                            });
                          } else {
                            const role = find(user.roles, {
                              name: "fwd_viewer",
                              arguments: {
                                org_id: selectedOrg,
                              },
                            });
                            if (role) {
                              deleteRole.mutate(role.id);
                            }
                          }
                        }}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </ul>
        )}
      </Modal.Content>
    </Modal>
  );
};
